import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';

type Role = 'owner' | 'tenant' | 'caretaker';

interface Residence {
  id: number;
  street_name: string;
  street_number: string;
  postal_code: string;
  city: string;
}

interface JoinEntry {
  selectedResidence: Residence | null;
  unit: string;
  role: Role;
  inheritAddress?: boolean;
}

interface NoResidenceJoinProps {
  allowAddAnother?: boolean;
}

export function NoResidenceJoin({ allowAddAnother = true }: NoResidenceJoinProps) {
  const [entries, setEntries] = useState<JoinEntry[]>([{ selectedResidence: null, unit: '', role: 'owner', inheritAddress: false }]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerms, setSearchTerms] = useState<string[]>(['']);
  const [filteredResidences, setFilteredResidences] = useState<Residence[][]>([[]]);
  const [showDropdowns, setShowDropdowns] = useState<boolean[]>([false]);

  // Debouncet søk via RPC for hver søketerm
  useEffect(() => {
    let isCancelled = false;
    const handler = setTimeout(async () => {
      try {
        const results: Residence[][] = await Promise.all(
          searchTerms.map(async (term) => {
            const { data, error } = await supabase.rpc('search_residences', {
              q: term || '',
              limit_num: 10
            });
            if (error) {
              console.error('Feil ved adresse-søk:', error);
              return [] as Residence[];
            }
            return (data || []) as Residence[];
          })
        );
        if (!isCancelled) {
          setFilteredResidences(results);
        }
      } catch (e) {
        console.error('Uventet feil ved adresse-søk:', e);
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(handler);
    };
  }, [searchTerms]);

  // Lukk dropdown når man klikker utenfor
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (!target.closest('.address-dropdown')) {
        setShowDropdowns(prev => prev.map(() => false));
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function updateEntry(index: number, patch: Partial<JoinEntry>) {
    setEntries(prev => prev.map((e, i) => i === index ? { ...e, ...patch } : e));
  }

  function addAnother() {
    setEntries(prev => [...prev, { selectedResidence: null, unit: '', role: 'owner', inheritAddress: false }]);
    setSearchTerms(prev => [...prev, '']);
    setShowDropdowns(prev => [...prev, false]);
    setFilteredResidences(prev => [...prev, []]);
  }

  function updateSearchTerm(index: number, term: string) {
    setSearchTerms(prev => prev.map((t, i) => i === index ? term : t));
    // Åpne dropdown for gjeldende index
    setShowDropdowns(prev => {
      const next = prev.slice();
      next[index] = true;
      return next;
    });
  }

  function selectResidence(index: number, residence: Residence) {
    updateEntry(index, { selectedResidence: residence });
    setSearchTerms(prev => prev.map((term, i) => i === index ? `${residence.street_name} ${residence.street_number}, ${residence.postal_code}, ${residence.city}` : term));
    setShowDropdowns(prev => prev.map((show, i) => i === index ? false : show));
  }


  async function handleSubmit() {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (entries.length === 0) {
      setErrorMessage('Ingen tilhørigheter å sende.');
      return;
    }

    // Valider at alle entries har valgt bofellesskap
    const invalidEntries = entries.filter(entry => !entry.selectedResidence);
    if (invalidEntries.length > 0) {
      setErrorMessage('Alle tilhørigheter må ha en valgt adresse.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user?.email) {
        setErrorMessage('Kunne ikke hente innlogget bruker. Vennligst prøv igjen.');
        setSubmitting(false);
        return;
      }

      // authData.user.email tilgjengelig ved behov for tilbakemelding

      // Prosesser alle innsendte tilhørigheter
      const results = await Promise.all(entries.map(async (entry) => {
        if (!entry.selectedResidence) {
          return { ok: false as const, message: 'Ingen bofellesskap valgt.' };
        }

        // Viktig: Unngå apartment-oppslag (RLS/406). Policy tillater apartment_id = null.

        // Kall SECURITY DEFINER RPC som håndterer RLS og innsetting server-side
        const { error: rpcError } = await supabase.rpc('request_residence_invite', {
          p_residence_id: entry.selectedResidence.id,
          p_role: entry.role,
          p_unit_text: entry.unit || null
        });

        if (rpcError) {
          const friendly = rpcError.code === '23505'
            ? 'Det finnes allerede en aktiv forespørsel/invitasjon for denne e‑posten.'
            : rpcError.message || 'Ukjent feil ved sending av forespørsel';
          return { ok: false as const, message: friendly };
        }

        return { ok: true as const };
      }));

      const failures = results.filter(r => !r.ok) as Array<{ ok: false; message: string }>;
      if (failures.length > 0) {
        setErrorMessage(failures.map(f => f.message).join(' '));
      }

      if (failures.length < results.length) {
        setSuccessMessage('Forespørsel sendt til styret. Du får svar så snart det er behandlet.');
      }
    } catch {
      setErrorMessage('Uventet feil. Prøv igjen senere.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white text-gray-900 rounded-2xl shadow-sm border p-8 sm:p-12">
      <h1 className="text-2xl sm:text-3xl font-semibold mb-3 text-center">Ingen tilhørighet enda</h1>
      <p className="text-gray-600 mb-8 text-center">Du er ikke knyttet til et bofellesskap enda. Meld deg inn eller vent på godkjenning.</p>

      <div className="max-w-xl mx-auto space-y-8">
        {errorMessage && (
          <div className="text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">{errorMessage}</div>
        )}
        {successMessage && (
          <div className="text-green-700 bg-green-50 border border-green-200 rounded p-3 text-sm">{successMessage}</div>
        )}
        {entries.map((entry, idx) => (
          <div key={idx} className="space-y-4">
            {entries.length > 1 && (
              <div className="text-sm font-medium text-gray-800">Tilhørighet {idx + 1}</div>
            )}
            {idx === entries.length - 1 && entries.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                  type="checkbox"
                  className="accent-condo-dark"
                  checked={Boolean(entry.inheritAddress)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEntries(prev => prev.map((e2, i2) => {
                      if (i2 !== idx) return e2;
                      const prevEntry = prev[idx - 1];
                      if (checked && prevEntry?.selectedResidence) {
                        return {
                          ...e2,
                          inheritAddress: true,
                          selectedResidence: prevEntry.selectedResidence
                        };
                      }
                      return { ...e2, inheritAddress: false };
                    }));
                    if (checked && entries[idx - 1]?.selectedResidence) {
                      setSearchTerms(prev => prev.map((term, i) => 
                        i === idx ? `${entries[idx - 1].selectedResidence?.street_name} ${entries[idx - 1].selectedResidence?.street_number}, ${entries[idx - 1].selectedResidence?.postal_code}, ${entries[idx - 1].selectedResidence?.city}` : term
                      ));
                    }
                  }}
                />
                Tilhørighet med samme adresse
              </label>
            )}
            
            {/* Adresse søkefelt */}
            {!entry.inheritAddress && (
              <div className="relative address-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input 
                  value={searchTerms[idx] || ''} 
                  onChange={(e) => updateSearchTerm(idx, e.target.value)}
                  onFocus={() => setShowDropdowns(prev => prev.map((show, i) => i === idx ? true : show))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark" 
                  placeholder="Søk etter adresse..." 
                />
                
                {/* Dropdown med søkeresultater */}
                {showDropdowns[idx] && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {(!filteredResidences[idx] || filteredResidences[idx].length === 0) && (
                      <div className="px-3 py-2 text-sm text-gray-500">Ingen treff</div>
                    )}
                    {filteredResidences[idx] && filteredResidences[idx].length > 0 && (
                      filteredResidences[idx].slice(0, 10).map((residence) => (
                        <button
                          key={residence.id}
                          type="button"
                          onClick={() => selectResidence(idx, residence)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {residence.street_name} {residence.street_number}
                          </div>
                          <div className="text-sm text-gray-600">
                            {residence.postal_code}, {residence.city}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Boenhetsnr og Rolle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Boenhetsnummer</label>
                <input value={entry.unit} onChange={(e) => updateEntry(idx, { unit: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark" placeholder="F.eks. 203" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
                <select value={entry.role} onChange={(e) => updateEntry(idx, { role: e.target.value as Role })} className="w-full px-3 py-2 border rounded-lg focus:ring-1 focus:outline-none focus:ring-condo-dark">
                  <option value="owner">Eier</option>
                  <option value="tenant">Leietaker</option>
                  <option value="caretaker">Vaktmester</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {allowAddAnother && (
          <div className="space-y-2">
            <button type="button" className="text-sm text-condo-dark hover:underline" onClick={addAnother}>
              + Legg til en til tilhørighet
            </button>
          </div>
        )}

        <Button className="w-full" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Sender…' : 'Send forespørsel til styret'}
        </Button>
      </div>
    </div>
  );
}
